import { createSelector, createSlice } from '@reduxjs/toolkit';
import * as R from 'ramda';
import { formatISO } from 'date-fns/fp';

// Utils
import { NO_END_DATE } from 'feature/loading-management/utils/const';
import { getStringifiedQueryParams } from 'utils/helpers/fetch';
import http from 'utils/http';

const initialState = {
  authorizationBy: 'location',
  forceClose: false,
  hasUnsavedChanges: false,
  inventoryOwner: {},
  params: {
    authorizationNumber: undefined,
    authorizationType: 'UNLIMITED',
    authorizedVolume: undefined,
    endDate: formatISO(NO_END_DATE),
    inventoryOwnerSelfAuthorizationIds: [],
    renewalVolume: undefined,
    supplierIdName: undefined,
  },
  shouldRefetchUnauthorizedList: true,
  submittingBatch: false,
  supplierList: [],
  unauthorizedList: [],
  unauthorizedListPending: false,
  viewById: undefined,
  viewByList: [],
  viewByListFetched: false,
};

const clearStepTwoSelections = unauthorizedList =>
  R.map(item => R.assoc('selected', false, item), unauthorizedList || []);

const markStepOneAsChanged = state => ({
  ...state,
  shouldRefetchUnauthorizedList: true,
  unauthorizedList: clearStepTwoSelections(state.unauthorizedList),
});

const reducers = {
  resetSupplierAuthWizardData: R.always(initialState),
  setBatchEnabledOptions: (updates, state) => {
    const idKey = `${state.authorizationBy === 'location' ? 'product' : 'location'}Id`;
    const updateMap = new Map(updates.map(({ id, selected }) => [parseInt(id), selected]));
    return R.assoc(
      'unauthorizedList',
      state.unauthorizedList.map(item =>
        updateMap.has(item[idKey]) ? { ...item, selected: updateMap.get(item[idKey]) } : item
      ),
      state
    );
  },
  setEnabledOptions: ({ id, selected }, state) => {
    const index = R.findIndex(
      R.propEq(`${state.authorizationBy === 'location' ? 'product' : 'location'}Id`, parseInt(id)),
      state.unauthorizedList
    );
    return R.assocPath(['unauthorizedList', index, 'selected'], selected, state);
  },
  setForceClose: R.assoc('forceClose'),
  setHasUnsavedChanges: R.assoc('hasUnsavedChanges'),
  setParams: (params, state) => {
    const nextState = R.converge(R.mergeDeepLeft, [R.objOf('params'), R.nthArg(1)])(params, state);
    return nextState;
  },
  setShouldRefetchUnauthorizedList: R.assoc('shouldRefetchUnauthorizedList'),
  setSubmittingBatch: R.assoc('submittingBatch'),
  setSupplierList: R.assoc('supplierList'),
  setUnauthorizedList: (unauthorizedList, state) => ({
    ...state,
    shouldRefetchUnauthorizedList: false,
    unauthorizedList,
  }),
  setUnauthorizedListPending: R.assoc('unauthorizedListPending'),
  setViewByList: R.assoc('viewByList'),
  setViewByListFetched: R.assoc('viewByListFetched'),
  setWizardDetails: ({ name, value }, state) => {
    const nextState = R.assoc(name, value, state);
    const stepOneFields = ['authorizationBy', 'inventoryOwner', 'viewById'];

    if (R.includes(name, stepOneFields)) {
      return markStepOneAsChanged(nextState);
    }

    return nextState;
  },
};

const { actions, reducer } = createSlice({
  initialState,
  name: 'createNewSupplierAuth',
  // eslint-disable-next-line react-hooks/rules-of-hooks
  reducers: R.map(singleReducer => R.flip(R.useWith(singleReducer, [R.prop('payload')])), reducers),
});

export const {
  resetSupplierAuthWizardData,
  setEnabledOptions,
  setForceClose,
  setHasUnsavedChanges,
  setParams,
  setBatchEnabledOptions,
  setShouldRefetchUnauthorizedList,
  setSubmittingBatch,
  setSupplierList,
  setUnauthorizedList,
  setUnauthorizedListPending,
  setViewByListFetched,
  setViewByList,
  setWizardDetails,
} = actions;

// createNewSupplierAuthPath :: Object -> Object
const createNewSupplierAuthPath = R.path(['loadingManagement', 'createNewSupplierAuth']);

// Step 1 Selectors
const getViewByListSelector = state => state.loadingManagement.createNewSupplierAuth.viewByList;
const getAuthBySelector = state => state.loadingManagement.createNewSupplierAuth.authorizationBy;
export const getViewByListOptions = createSelector(
  getViewByListSelector,
  getAuthBySelector,
  (items, authBy) => {
    const id = `${authBy}Id`;
    return R.map(
      item => ({
        label: item.nameLong,
        value: item[id],
      }),
      items
    );
  }
);

const getSupplierListSelector = state => state.loadingManagement.createNewSupplierAuth.supplierList;
export const getSupplierListOptions = createSelector(getSupplierListSelector, items =>
  R.map(
    ({ dataCompanyId, companyCustomerIdName, nameShort }) => ({
      dataCompanyId,
      label: nameShort,
      value: companyCustomerIdName,
    }),
    items
  )
);

// Step 2 Selectors

export const getUnauthorizedList = R.o(R.prop('unauthorizedList'), createNewSupplierAuthPath);

export const getInactiveListOptions = createSelector(
  getUnauthorizedList,
  state => state.loadingManagement.createNewSupplierAuth.authorizationBy,
  (unauthorizedList, authorizationBy) =>
    R.map(
      ({
        inventoryOwnerSelfAuthorizationId,
        productId,
        locationId,
        productIdName,
        nameLong,
        pidx,
        proprietary,
        selected,
      }) => ({
        inventoryOwnerSelfAuthorizationId,
        label: nameLong,
        pidx,
        productIdName,
        proprietary,
        selected: !!selected,
        value: authorizationBy === 'location' ? productId : locationId,
      }),
      unauthorizedList
    )
);

export const getAllEnabled = createSelector(getUnauthorizedList, R.all(R.prop('selected')));
export const getStepTwoDisableContinue = createSelector(
  getUnauthorizedList,
  R.none(R.prop('selected'))
);

// Step 3 Selectors

// getLocation :: Object -> Object
export const getLocation = createSelector(
  R.o(R.prop('viewById'), createNewSupplierAuthPath),
  R.o(R.prop('viewByList'), createNewSupplierAuthPath),
  // eslint-disable-next-line react-hooks/rules-of-hooks
  R.useWith(R.find, [R.propEq('locationId')])
);

// getProduct :: Object -> Object
export const getProduct = createSelector(
  R.o(R.prop('viewById'), createNewSupplierAuthPath),
  R.o(R.prop('viewByList'), createNewSupplierAuthPath),
  // eslint-disable-next-line react-hooks/rules-of-hooks
  R.useWith(R.find, [R.propEq('productId')])
);

// getParams :: Object -> Object
export const getParams = createSelector(
  R.o(R.prop('params'), createNewSupplierAuthPath),
  R.identity
);

// getSupplier :: Object -> Object
export const getSupplier = createSelector(
  R.o(R.path(['params', 'supplierIdName']), createNewSupplierAuthPath),
  R.o(R.prop('supplierList'), createNewSupplierAuthPath),
  // eslint-disable-next-line react-hooks/rules-of-hooks
  R.useWith(R.find, [R.propEq('companyCustomerIdName')])
);

// getInventoryOwner :: Object -> Object
export const getInventoryOwner = createSelector(
  R.o(R.prop('inventoryOwner'), createNewSupplierAuthPath),
  R.identity
);

// getSelectedProducts :: Object -> Array
export const getSelectedProducts = createSelector(
  getInactiveListOptions,
  R.filter(R.prop('selected'))
);

export const getAuthorizationBy = createSelector(
  R.o(R.prop('authorizationBy'), createNewSupplierAuthPath),
  R.identity
);

export const getViewByListFetched = createSelector(
  R.o(R.prop('viewByListFetched'), createNewSupplierAuthPath),
  R.identity
);

// Actions

export const fetchLocations = queryParams => async dispatch => {
  const queryString = getStringifiedQueryParams(queryParams);
  dispatch(setViewByListFetched(false));
  http
    .get(`supplier-authorizations/get-locations?${queryString}`)
    .then(({ data }) => {
      dispatch(setViewByList(data));
    })
    .finally(() => {
      dispatch(setViewByListFetched(true));
    });
};

export const fetchProducts = queryParams => async dispatch => {
  const queryString = getStringifiedQueryParams(queryParams);
  dispatch(setViewByListFetched(false));
  http
    .get(`supplier-authorizations/get-products?${queryString}`)
    .then(({ data }) => {
      dispatch(setViewByList(data));
    })
    .finally(() => {
      dispatch(setViewByListFetched(true));
    });
};

export const fetchSuppliers = queryParams => async dispatch => {
  const queryString = getStringifiedQueryParams(queryParams);
  http.get(`supplier-authorizations/get-suppliers?${queryString}`).then(({ data }) => {
    dispatch(setSupplierList(data));
  });
};

export const fetchUnauthorizedProductsOrLocations = () => async (dispatch, getState) => {
  const {
    authorizationBy,
    shouldRefetchUnauthorizedList,
    unauthorizedList,
    viewById,
    inventoryOwner,
    params: { supplierIdName },
    supplierList,
  } = getState().loadingManagement.createNewSupplierAuth;

  if (!shouldRefetchUnauthorizedList && unauthorizedList.length) {
    return 'cached';
  }

  const supplier = R.find(R.propEq('companyCustomerIdName', supplierIdName), supplierList);
  const { dataCompanyId, companyCustomerIdName } = supplier;

  const queryParams = {
    [`${authorizationBy}Id`]: viewById,
    inventoryOwner: {
      companyCustomerIdName: inventoryOwner.companyCustomerIdName,
      dataCompanyId: inventoryOwner.dataCompanyId,
    },
    supplier: { companyCustomerIdName, dataCompanyId },
  };
  dispatch(setUnauthorizedListPending(true));
  const uniqueQueryString =
    authorizationBy === 'location'
      ? '/get-location-unauthorized-products?'
      : '/get-product-unauthorized-locations?';

  const queryString = getStringifiedQueryParams(queryParams);
  return http
    .get(`supplier-authorizations${uniqueQueryString}${queryString}`)
    .then(({ data }) => {
      dispatch(setUnauthorizedList(data));
      return 'fetched';
    })
    .finally(() => {
      dispatch(setUnauthorizedListPending(false));
    });
};

export const confirmCloseDialog = () => dispatch => {
  dispatch(setForceClose(true));
};

export const createSupplierAuthorization = params => () => {
  const { value: productId } = params;

  return http
    .post(`supplier-authorizations/create`, params)
    .then(R.always({ error: '', productId, success: true }))
    .catch(error => {
      if (error.response?.data) {
        return {
          error: error.response.data,
          productId,
          successful: false,
        };
      }
      if (error.request?.statusText) {
        return {
          error: error.request.statusText,
          productId,
          successful: false,
        };
      }
      return {
        error: error.message || 'Unknown Error',
        productId,
        successful: false,
      };
    });
};

export default reducer;
